import { z } from "zod";

/**
 * Permission catalog (Req 34, design "permission-based authorization").
 *
 * Handlers check PERMISSIONS, not raw roles. Roles are stable permission
 * bundles (see roles-permissions.ts). Format is `<resource>.<verb>`.
 *
 * Only permissions the product surfaces in v1 (P0/P1) are listed. New surfaces
 * add permissions here; do not scatter ad-hoc role checks through handlers.
 */
export const PermissionSchema = z.enum([
  // Overview / findings (read surfaces)
  "overview.read",
  "findings.read",
  // Estate / portfolio
  "estate.read",
  "estate.write",
  "portfolio.read",
  "portfolio.write",
  // Legacy intake
  "import.read",
  "import.write",
  // Assessment / readiness / waves
  "assessment.read",
  "assessment.write",
  "readiness.read",
  "readiness.write",
  "wave.read",
  "wave.write",
  // Dual-run / cutover / decommission
  "dualrun.read",
  "cutover.write",
  "decommission.read",
  "decommission.write",
  // Recommendations / actions / approvals / exceptions
  "recommendation.read",
  "action.read",
  "action.write",
  "action.approve",
  "exception.write",
  // Value ledger
  "ledger.read",
  "ledger.verify",
  // Explorer / reports
  "explorer.read",
  "explorer.write",
  "report.read",
  // Governance
  "budget.read",
  "budget.write",
  "metadata.read",
  // AWS connections / ingestion
  "aws_connection.read",
  "aws_connection.write",
  "ingestion.read",
  // Background jobs (manual "run now")
  "job.run",
  // Commercial: plan/billing, alerts, consented expert assistance
  "billing.read",
  "billing.manage",
  "notification.read",
  "support.request",
  // Admin
  "tenant.admin",
  "user.admin",
  "audit.read",
]);
export type Permission = z.infer<typeof PermissionSchema>;

export const ALL_PERMISSIONS: readonly Permission[] = PermissionSchema.options;
